import {
  BG_STYLES,
  COMMON_STYLES,
  LazyImage,
  TEXT_STYLES,
  TYPOGRAPHY,
  VARIANTS_STYLES,
} from "@dehaat/dds";
import { Crop } from "app/farmer360/models/crop";
import React, { useEffect, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  ImageStyle,
  TouchableOpacity,
  TextStyle,
  StyleProp,
} from "react-native";

interface Props {
  crop: Crop;
  containerStyle?: Object;
  imageContainerStyle?: Object;
  activeImagecontainerStyle?: Object;
  imageStyle?: ImageStyle;
  textStyle?: Object;
  isIcon?: boolean;
  icon?: JSX.Element;
  iconContainerStyle?: Object;
  isDisabled?: boolean;
  onClick?: (arg: Crop) => void;
  onIconClick?: (arg: Crop) => void;
  isImageSelected: Boolean;
  label?: string | number;
}

interface FallbackIconProps {
  text: string;
  containerStyle?: ImageStyle;
  textStyle?: StyleProp<TextStyle>;
}

export const FallbackIcon = ({
  text,
  containerStyle,
  textStyle,
}: FallbackIconProps) => {
  return (
    <View
      style={[
        COMMON_STYLES.alignItemsCenter,
        COMMON_STYLES.justifyCenter,
        styles.imageContainer,
        BG_STYLES.bgWhite,
        containerStyle,
      ]}
    >
      <Text style={[styles.fallbackTextStyle, textStyle]}>{text}</Text>
    </View>
  );
};

const RoundImageWithIcon = ({
  crop,
  containerStyle,
  imageContainerStyle,
  activeImagecontainerStyle,
  imageStyle,
  textStyle,
  isIcon = false,
  icon,
  iconContainerStyle,
  isDisabled = false,
  onClick,
  onIconClick,
  isImageSelected,
  label,
}: Props) => {
  const [isIconVisible, setIsIconVisible] = useState(isIcon);

  useEffect(() => {
    if (isImageSelected) {
      setIsIconVisible(true);
    } else {
      setIsIconVisible(false);
    }
  }, [crop, crop.id, isImageSelected]);

  const handleClick = () => {
    if (onClick) {
      onClick(crop);
    }
  };

  const handleIconClick = () => {
    if (onIconClick) {
      onIconClick(crop);
    }
  };

  return (
    <TouchableOpacity
      onPress={handleClick}
      disabled={isDisabled}
      style={[COMMON_STYLES.alignItemsCenter, containerStyle]}
    >
      <View>
        {isIconVisible ? (
          <TouchableOpacity
            onPress={handleIconClick}
            style={iconContainerStyle}
          >
            {icon ? icon : null}
          </TouchableOpacity>
        ) : null}
        <View
          style={[
            styles.imageContainer,
            !!label && COMMON_STYLES.relative,
            imageContainerStyle,
            isIconVisible ? activeImagecontainerStyle : null,
          ]}
        >
          <LazyImage
            imageUrl={crop.image_url}
            fallBackIcon={
              <FallbackIcon text={crop.name[0]} containerStyle={imageStyle} />
            }
            imgStyles={[BG_STYLES.bgWhite, imageStyle]}
          />
        </View>
        {label ? (
          <View
            style={[
              COMMON_STYLES.absolute,
              COMMON_STYLES.left,
              COMMON_STYLES.right,
              COMMON_STYLES.alignItemsCenter,
              styles.labelStyle,
            ]}
          >
            <View
              style={[
                BG_STYLES.bgBlack,
                COMMON_STYLES["ph1/2"],
                COMMON_STYLES["pv1/4"],
                COMMON_STYLES["br1/2"],
              ]}
            >
              <Text
                style={[
                  TYPOGRAPHY.CAPTION1,
                  VARIANTS_STYLES.MEDIUM,
                  TEXT_STYLES.textWhite,
                  COMMON_STYLES.textCenter,
                ]}
              >
                {Number(label) < 10 ? `0${label}` : label}
              </Text>
            </View>
          </View>
        ) : null}
      </View>
      <Text
        numberOfLines={1}
        style={[
          COMMON_STYLES["mt1/4"],
          !!label && COMMON_STYLES["mt1/2"],
          textStyle,
        ]}
      >
        {crop.name}
      </Text>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  imageContainer: {
    overflow: "hidden",
    borderRadius: 9999,
  },
  fallbackTextStyle: {
    fontSize: 32,
    fontWeight: "800",
  },
  labelStyle: {
    bottom: -8,
  },
});

export default RoundImageWithIcon;
